/**
 * ENH-009 Scheduler page.
 *
 * Renders a FullCalendar-based unified view of every event that has a date on
 * it in the app — vet visits, care events, vaccinations, trips, recovery
 * restrictions, training + custom events (scheduler_events collection), and
 * staff / owner away. See /app/backend/routers/scheduler.py for the merge
 * logic; this page is a thin visualisation layer over that endpoint.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, X, Save, Trash2, Filter as FilterIcon, Search } from "lucide-react";

const EVENT_TYPES = [
  { key: "vet_visit",         label: "Vet visits", icon: "🩺" },
  { key: "care_shoeing",      label: "Shoeing",    icon: "🔨" },
  { key: "care_deworming",    label: "Deworming",  icon: "💊" },
  { key: "care_dental",       label: "Dental",     icon: "🦷" },
  { key: "vaccination",       label: "Vaccines",   icon: "💉" },
  { key: "recovery",          label: "Recovery",   icon: "🔴" },
  { key: "trip",              label: "Trips",      icon: "🚛" },
  { key: "training",          label: "Training",   icon: "🏋️" },
  { key: "custom",            label: "Custom",     icon: "📌" },
  { key: "staff_leave",       label: "Staff leave", icon: "🌴" },
  { key: "owner_away",        label: "Owner away",  icon: "🌴" },
];

const WRITE_ROLES = new Set(["admin", "manager", "super_admin"]);
const LS_HORSES = "stablos:scheduler:horses";
const LS_TYPES  = "stablos:scheduler:types";
const LS_LEGEND = "stablos:scheduler:legend_open";
const LS_VIEW   = "stablos_scheduler_view";
const VALID_VIEWS = new Set(["dayGridMonth", "timeGridWeek", "timeGridDay", "listWeek"]);

const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;

const readInitialView = () => {
  try {
    const v = localStorage.getItem(LS_VIEW);
    if (v && VALID_VIEWS.has(v)) return v;
  } catch { /* ignore */ }
  return "dayGridMonth";
};

export default function Scheduler() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const canWrite = WRITE_ROLES.has(role);
  const calendarRef = useRef(null);

  const [horses, setHorses] = useState([]);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [range, setRange] = useState(null);
  const [selectedHorseIds, setSelectedHorseIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_HORSES) || "[]"); } catch { return []; }
  });
  const [activeTypes, setActiveTypes] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_TYPES) || "null");
      return saved && Array.isArray(saved) ? new Set(saved) : new Set(EVENT_TYPES.map((t) => t.key));
    } catch { return new Set(EVENT_TYPES.map((t) => t.key)); }
  });
  const [legendOpen, setLegendOpen] = useState(() => localStorage.getItem(LS_LEGEND) === "1");
  const [showAdd, setShowAdd] = useState(false);
  const [modalPrefill, setModalPrefill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mobile, setMobile] = useState(isMobile());
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setMobile(isMobile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { localStorage.setItem(LS_HORSES, JSON.stringify(selectedHorseIds)); }, [selectedHorseIds]);
  useEffect(() => { localStorage.setItem(LS_TYPES, JSON.stringify(Array.from(activeTypes))); }, [activeTypes]);
  useEffect(() => { localStorage.setItem(LS_LEGEND, legendOpen ? "1" : "0"); }, [legendOpen]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/scheduler/horses");
        setHorses(data || []);
      } catch (e) { setError(formatApiError(e)); }
    })();
  }, []);

  const fetchEvents = useCallback(async (fromISO, toISO) => {
    if (!fromISO || !toISO) return;
    setError("");
    try {
      const params = { start: fromISO, end: toISO };
      if (selectedHorseIds.length) params.horse_ids = selectedHorseIds;
      if (activeTypes.size < EVENT_TYPES.length) params.event_types = Array.from(activeTypes);
      const { data } = await api.get("/scheduler/events", {
        params,
        paramsSerializer: (p) => Object.entries(p).flatMap(([k, v]) =>
          Array.isArray(v) ? v.map((x) => `${k}=${encodeURIComponent(x)}`) : [`${k}=${encodeURIComponent(v)}`],
        ).join("&"),
      });
      setEvents(data || []);
    } catch (e) { setError(formatApiError(e)); setEvents([]); }
  }, [selectedHorseIds, activeTypes]);

  useEffect(() => {
    if (range) fetchEvents(range.start, range.end);
  }, [range, fetchEvents]);

  const onDatesSet = (arg) => {
    const start = arg.startStr?.slice(0, 10);
    const end = arg.endStr ? arg.endStr.slice(0, 10) : arg.startStr.slice(0, 10);
    setRange({ start, end });
    try {
      if (arg.view?.type && VALID_VIEWS.has(arg.view.type)) {
        localStorage.setItem(LS_VIEW, arg.view.type);
      }
    } catch { /* ignore */ }
  };

  // Client-side search filtering across common text fields.
  const searchNeedle = search.trim().toLowerCase();
  const visibleEvents = useMemo(() => {
    if (!searchNeedle) return events;
    return events.filter((e) => {
      const d = e.detail || {};
      const haystack = [
        e.title, e.horse_name,
        d.vet_name, d.trip_name, d.location,
        d.vaccine_name, d.care_type,
        d.administered_by, d.reason, d.leave_reason,
        d.notes, d.comment, d.restriction_type, d.training_type,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(searchNeedle);
    });
  }, [events, searchNeedle]);

  const fcEvents = useMemo(() => visibleEvents.map((e) => {
    const allDay = e.allDay !== false;
    const endOut = e.end ? (allDay ? addOneDay(e.end) : e.end) : undefined;
    return {
      id: e.id,
      title: e.title,
      start: e.start,
      end: endOut,
      allDay,
      backgroundColor: e.color,
      borderColor: e.color,
      textColor: "#FFFFFF",
      extendedProps: e,
    };
  }), [visibleEvents]);

  const horseById = useMemo(() => {
    const m = new Map();
    horses.forEach((h) => m.set(h.id, h));
    return m;
  }, [horses]);

  const toggleHorse = (id) => setSelectedHorseIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
  );
  const toggleType = (key) => setActiveTypes((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const refresh = () => { if (range) fetchEvents(range.start, range.end); };

  // Single vs double tap handling: double-tap → Day view; single tap → Add
  // Event (admin/manager only), fired after a short delay to let the second
  // tap arrive first.
  const clickRef = useRef({ dateStr: null, time: 0, timer: null });

  const onDateClick = (arg) => {
    const now = Date.now();
    const DOUBLE_MS = 450;
    const prev = clickRef.current;
    if (prev.dateStr === arg.dateStr && (now - prev.time) < DOUBLE_MS) {
      if (prev.timer) clearTimeout(prev.timer);
      clickRef.current = { dateStr: null, time: 0, timer: null };
      const apiRef = calendarRef.current?.getApi();
      if (apiRef) {
        apiRef.changeView("timeGridDay", arg.date);
      }
      return;
    }
    if (prev.timer) clearTimeout(prev.timer);
    const captured = arg;
    const timer = setTimeout(() => {
      clickRef.current = { dateStr: null, time: 0, timer: null };
      if (!canWrite) return;
      const isoDate = captured.dateStr.slice(0, 10);
      const hhmm = !captured.allDay && captured.date ? `${String(captured.date.getHours()).padStart(2, "0")}:${String(captured.date.getMinutes()).padStart(2, "0")}` : "";
      setModalPrefill({
        mode: "add",
        kind: "custom",
        start_date: isoDate,
        end_date: "",
        all_day: captured.allDay !== false,
        start_time: hhmm,
        end_time: "",
      });
      setShowAdd(true);
    }, DOUBLE_MS);
    clickRef.current = { dateStr: arg.dateStr, time: now, timer };
  };

  const openAdd = () => {
    setModalPrefill(null);
    setShowAdd(true);
  };

  return (
    <div data-testid="scheduler-page" className="pb-4">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-2">
        <div>
          <div className="se-label">Planning</div>
          <h1 className="font-heading mt-0.5" style={{ fontSize: 24 }}>Scheduler</h1>
        </div>
        {canWrite && (
          <button className="se-btn" onClick={openAdd} data-testid="scheduler-add-event">
            <Plus size={16} /> Add event
          </button>
        )}
      </div>

      {/* Filter row */}
      <div className="se-card mb-2" style={{ padding: 10 }} data-testid="scheduler-filters">
        {/* Row 1: Horses (grow) + Search (shrink) */}
        <div className="flex items-center gap-2 flex-nowrap">
          {role !== "owner" && horses.length > 0 && (
            <div className="min-w-0 flex-1 flex items-center gap-2">
              <span className="se-label shrink-0" style={{ fontSize: 10 }}>HORSES</span>
              <div className="flex gap-1.5 overflow-x-auto flex-nowrap" style={{ scrollbarWidth: "thin" }} data-testid="scheduler-horse-filter">
                <button
                  type="button"
                  onClick={() => setSelectedHorseIds([])}
                  className="text-xs px-2 py-1 rounded-full whitespace-nowrap shrink-0"
                  style={{
                    background: selectedHorseIds.length === 0 ? "#7C5C3A" : "transparent",
                    color: selectedHorseIds.length === 0 ? "#F5EFE6" : "#7C5C3A",
                    border: "0.5px solid rgba(124,92,58,0.35)",
                  }}
                >All</button>
                {horses.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => toggleHorse(h.id)}
                    className="text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 whitespace-nowrap shrink-0"
                    style={{
                      background: selectedHorseIds.includes(h.id) ? h.calendar_color : "transparent",
                      color: selectedHorseIds.includes(h.id) ? "#FFFFFF" : "#3B2F1E",
                      border: `0.5px solid ${h.calendar_color}`,
                    }}
                    data-testid={`scheduler-horse-chip-${h.id}`}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: 4, background: h.calendar_color, display: "inline-block" }} />
                    {h.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {role === "owner" && <div className="flex-1" />}

          {/* Search */}
          <div className="shrink-0">
            {mobile && !searchOpen && !search ? (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="se-btn se-btn-ghost"
                style={{ padding: "6px 10px" }}
                data-testid="scheduler-search-toggle"
                aria-label="Open search"
              >
                <Search size={14} />
              </button>
            ) : (
              <div className="relative">
                <Search size={12} style={{ position: "absolute", top: "50%", left: 8, transform: "translateY(-50%)", color: "#8C9E8A" }} />
                <input
                  type="text"
                  className="se-input"
                  style={{ paddingLeft: 26, paddingRight: 28, minWidth: mobile ? 160 : 220, height: 32, fontSize: 13 }}
                  placeholder="Horse, vet, trip, staff…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus={mobile && searchOpen}
                  data-testid="scheduler-search"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => { setSearch(""); if (mobile) setSearchOpen(false); }}
                    className="absolute inset-y-0 right-2 flex items-center"
                    aria-label="Clear search"
                    data-testid="scheduler-search-clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Event types (one scrolling row) */}
        <div className="flex items-center gap-2 mt-2">
          <span className="se-label shrink-0 inline-flex items-center gap-1" style={{ fontSize: 10 }}>
            <FilterIcon size={10} /> TYPES
          </span>
          <div className="flex gap-1.5 overflow-x-auto flex-nowrap flex-1" style={{ scrollbarWidth: "thin" }} data-testid="scheduler-type-filter">
            {EVENT_TYPES.map((t) => {
              const active = activeTypes.has(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggleType(t.key)}
                  className="text-xs px-2 py-1 rounded-full whitespace-nowrap shrink-0"
                  style={{
                    background: active ? "#7C5C3A" : "transparent",
                    color: active ? "#F5EFE6" : "#7C5C3A",
                    border: "0.5px solid rgba(124,92,58,0.35)",
                  }}
                  data-testid={`scheduler-type-${t.key}`}
                >{t.icon} {t.label}</button>
              );
            })}
          </div>
          {horses.length > 0 && (
            <button
              type="button"
              className="text-xs se-link shrink-0"
              onClick={() => setLegendOpen(!legendOpen)}
              data-testid="scheduler-legend-toggle"
              style={{ whiteSpace: "nowrap" }}
            >{legendOpen ? "Hide legend" : "Show legend"}</button>
          )}
        </div>

        {/* Legend (collapsed by default) */}
        {legendOpen && horses.length > 0 && (
          <div
            className="flex flex-wrap gap-x-3 gap-y-1 mt-2 pt-2"
            style={{ borderTop: "0.5px solid rgba(212,169,106,0.3)" }}
            data-testid="scheduler-legend"
          >
            {horses.map((h) => (
              <span key={h.id} className="inline-flex items-center gap-1 text-xs">
                <span style={{ width: 10, height: 10, borderRadius: 5, background: h.calendar_color, display: "inline-block" }} />
                {h.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="scheduler-error">
          {error}
        </div>
      )}

      {/* Calendar */}
      <div className="se-card p-2" style={{ minHeight: mobile ? "calc(100vh - 320px)" : "calc(100vh - 260px)" }} data-testid="scheduler-calendar">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView={readInitialView()}
          headerToolbar={mobile
            ? { left: "prev,next", center: "title", right: "dayGridMonth,timeGridDay,listWeek" }
            : { left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek" }}
          height={mobile ? "calc(100vh - 340px)" : "calc(100vh - 280px)"}
          nowIndicator
          editable={false}
          selectable={false}
          eventDisplay="block"
          dayMaxEvents={3}
          events={fcEvents}
          eventClick={(info) => setDetail(info.event.extendedProps)}
          dateClick={onDateClick}
          datesSet={onDatesSet}
          views={{
            listWeek: { listDayFormat: { weekday: "long", month: "long", day: "numeric" } },
          }}
        />
      </div>

      {showAdd && canWrite && (
        <AddEventModal
          horses={horses}
          prefill={modalPrefill}
          onClose={() => { setShowAdd(false); setModalPrefill(null); }}
          onSaved={() => { setShowAdd(false); setModalPrefill(null); refresh(); }}
        />
      )}
      {detail && (
        <EventDetailModal
          event={detail}
          canWrite={canWrite}
          horseById={horseById}
          mobile={mobile}
          onClose={() => setDetail(null)}
          onDeleted={() => { setDetail(null); refresh(); }}
          onEdit={(prefillObj) => {
            setDetail(null);
            setModalPrefill(prefillObj);
            setShowAdd(true);
          }}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Add / Edit event modal (admin/manager only)
// -----------------------------------------------------------------------------
function AddEventModal({ horses, prefill, onClose, onSaved }) {
  const isEdit = prefill?.mode === "edit";
  const [kind, setKind] = useState(prefill?.kind || "custom");

  const [staff, setStaff] = useState([]);
  const [owners, setOwners] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get("/users", { params: { status: "active" } }),
          api.get("/owners"),
        ]);
        setStaff((s.data || []).filter((u) => u.status === "active" && !u.is_deleted));
        setOwners((o.data || []).filter((x) => !x.is_deleted));
      } catch (_) { /* silent */ }
    })();
  }, []);

  const [customForm, setCustomForm] = useState(() => ({
    title:      prefill?.title      || "",
    start_date: prefill?.start_date || new Date().toISOString().slice(0, 10),
    end_date:   prefill?.end_date   || "",
    all_day:    prefill?.all_day !== undefined ? prefill.all_day : true,
    start_time: prefill?.start_time || "",
    end_time:   prefill?.end_time   || "",
    horse_ids:  Array.isArray(prefill?.horse_ids) ? prefill.horse_ids : (prefill?.horse_id ? [prefill.horse_id] : []),
    notes:      prefill?.notes      || "",
    scheduler_event_id: prefill?.scheduler_event_id || null,
  }));

  const [leaveForm, setLeaveForm] = useState(() => ({
    user_id:      prefill?.user_id      || "",
    start_date:   prefill?.leave_start  || "",
    end_date:     prefill?.leave_end    || "",
    reason:       prefill?.leave_reason || "",
  }));

  const [awayForm, setAwayForm] = useState(() => ({
    owner_id:     prefill?.owner_id     || "",
    start_date:   prefill?.leave_start  || "",
    end_date:     prefill?.leave_end    || "",
    reason:       prefill?.leave_reason || "",
  }));

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ownerHorseNames = useMemo(() => {
    const m = new Map();
    horses.forEach((h) => {
      if (!h.owner_id) return;
      const list = m.get(h.owner_id) || [];
      list.push(h.name);
      m.set(h.owner_id, list);
    });
    return m;
  }, [horses]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (kind === "custom") {
        if (!customForm.title.trim()) throw new Error("Title is required.");
        if (!customForm.start_date) throw new Error("Start date is required.");
        if (!customForm.all_day && !customForm.start_time) throw new Error("Start time is required when not all-day.");
        if (customForm.end_date && customForm.end_date < customForm.start_date) throw new Error("End date must be on or after start date.");
        const payload = {
          event_type: "custom",
          title: customForm.title.trim(),
          start_date: customForm.start_date,
          end_date: customForm.end_date || null,
          all_day: customForm.all_day,
          start_time: customForm.all_day ? null : (customForm.start_time || null),
          end_time:   customForm.all_day ? null : (customForm.end_time   || null),
          horse_ids: customForm.horse_ids,
          notes: customForm.notes.trim() || null,
        };
        if (isEdit && customForm.scheduler_event_id) {
          await api.patch(`/scheduler/events/${customForm.scheduler_event_id}`, payload);
        } else {
          await api.post("/scheduler/events", payload);
        }
      } else if (kind === "staff_leave") {
        if (!leaveForm.user_id) throw new Error("Please pick a staff member.");
        if (!leaveForm.start_date || !leaveForm.end_date) throw new Error("Start and end dates are required.");
        if (leaveForm.end_date < leaveForm.start_date) throw new Error("End date must be on or after start date.");
        await api.patch(`/users/${leaveForm.user_id}`, {
          leave_start:  leaveForm.start_date,
          leave_end:    leaveForm.end_date,
          leave_reason: leaveForm.reason.trim() || null,
        });
      } else if (kind === "owner_away") {
        if (!awayForm.owner_id) throw new Error("Please pick an owner.");
        if (!awayForm.start_date || !awayForm.end_date) throw new Error("Start and end dates are required.");
        if (awayForm.end_date < awayForm.start_date) throw new Error("End date must be on or after start date.");
        await api.patch(`/owners/${awayForm.owner_id}`, {
          leave_start:  awayForm.start_date,
          leave_end:    awayForm.end_date,
          leave_reason: awayForm.reason.trim() || null,
        });
      }
      onSaved();
    } catch (ex) {
      setErr(ex?.response ? formatApiError(ex) : (ex.message || "Save failed"));
    } finally { setBusy(false); }
  };

  const KIND_LABEL = { custom: "Custom event", staff_leave: "Staff leave", owner_away: "Owner away" };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.4)" }}>
      <form onSubmit={submit} className="se-card w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="scheduler-add-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading" style={{ fontSize: 18 }}>{isEdit ? `Edit ${KIND_LABEL[kind]}` : "Add event"}</h3>
          <button type="button" onClick={onClose} data-testid="scheduler-add-close"><X size={16} /></button>
        </div>

        {!isEdit && (
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="scheduler-kind-picker">
            {[
              { k: "custom",      icon: "📌", label: "Custom event" },
              { k: "staff_leave", icon: "🌴", label: "Staff leave" },
              { k: "owner_away",  icon: "👤", label: "Owner away" },
            ].map((opt) => (
              <button
                key={opt.k}
                type="button"
                onClick={() => setKind(opt.k)}
                data-testid={`scheduler-kind-${opt.k}`}
                className="p-3 rounded-xl text-center"
                style={{
                  background: kind === opt.k ? "#7C5C3A" : "transparent",
                  color: kind === opt.k ? "#F5EFE6" : "#3B2F1E",
                  border: `0.5px solid ${kind === opt.k ? "#7C5C3A" : "rgba(124,92,58,0.35)"}`,
                }}
              >
                <div style={{ fontSize: 22 }}>{opt.icon}</div>
                <div className="text-xs mt-1">{opt.label}</div>
              </button>
            ))}
          </div>
        )}

        {kind === "custom" && (
          <CustomEventFields
            horses={horses}
            form={customForm}
            setForm={setCustomForm}
          />
        )}

        {kind === "staff_leave" && (
          <LeaveFields
            options={staff.map((u) => ({ id: u.id, label: `${u.full_name} (${(u.role || "").replace(/_/g, " ")})` }))}
            selectorLabel="Staff member *"
            selectorTestId="scheduler-staff-picker"
            selectedId={leaveForm.user_id}
            onSelect={(id) => setLeaveForm({ ...leaveForm, user_id: id })}
            form={leaveForm}
            setForm={setLeaveForm}
            disabledSelector={isEdit}
          />
        )}

        {kind === "owner_away" && (
          <LeaveFields
            options={owners.map((o) => {
              const names = ownerHorseNames.get(o.id) || [];
              return { id: o.id, label: `${o.full_name}${names.length ? ` [${names.join(", ")}]` : ""}` };
            })}
            selectorLabel="Owner *"
            selectorTestId="scheduler-owner-picker"
            selectedId={awayForm.owner_id}
            onSelect={(id) => setAwayForm({ ...awayForm, owner_id: id })}
            form={awayForm}
            setForm={setAwayForm}
            disabledSelector={isEdit}
          />
        )}

        {err && <div className="text-xs mb-3 px-2 py-1 rounded" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="scheduler-add-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="scheduler-add-submit">
            <Save size={14} /> {busy ? "Saving…" : (isEdit ? "Save changes" : "Save event")}
          </button>
        </div>
      </form>
    </div>
  );
}

function CustomEventFields({ horses, form, setForm }) {
  const selectedCount = form.horse_ids.length;
  const toggle = (id) => setForm({
    ...form,
    horse_ids: form.horse_ids.includes(id) ? form.horse_ids.filter((x) => x !== id) : [...form.horse_ids, id],
  });
  const clearAll = () => setForm({ ...form, horse_ids: [] });
  return (
    <>
      <label className="se-label block mb-1">Title *</label>
      <input className="se-input mb-3" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="scheduler-add-title" required />

      <label className="se-label block mb-1">Date *</label>
      <input type="date" className="se-input mb-3" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="scheduler-add-start" />

      <label className="text-sm inline-flex items-center gap-2 mb-3">
        <input
          type="checkbox"
          checked={form.all_day}
          onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
          data-testid="scheduler-add-allday"
        />
        All-day
      </label>

      {!form.all_day && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Start time *</label>
            <input type="time" className="se-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required data-testid="scheduler-add-start-time" />
          </div>
          <div>
            <label className="se-label block mb-1">End time</label>
            <input type="time" className="se-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="scheduler-add-end-time" />
          </div>
        </div>
      )}

      <label className="se-label block mb-1">
        Linked horses — <span style={{ color: "#7C5C3A" }} data-testid="scheduler-add-horse-count">
          {selectedCount === 0 ? "No horse (stable event)" : `${selectedCount} horse${selectedCount === 1 ? "" : "s"} selected`}
        </span>
      </label>
      <div
        className="mb-3 rounded-xl"
        style={{ border: "0.5px solid rgba(124,92,58,0.35)", maxHeight: 160, overflowY: "auto" }}
        data-testid="scheduler-add-horse-list"
      >
        <label
          className="flex items-center gap-2 px-3 py-2 cursor-pointer"
          style={{ borderBottom: "0.5px solid rgba(212,169,106,0.3)", background: selectedCount === 0 ? "rgba(124,92,58,0.08)" : "transparent" }}
        >
          <input
            type="checkbox"
            checked={selectedCount === 0}
            onChange={clearAll}
            data-testid="scheduler-add-horse-none"
          />
          <span className="text-sm font-medium">No horse (stable event)</span>
        </label>
        {horses.map((h) => {
          const checked = form.horse_ids.includes(h.id);
          return (
            <label key={h.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer" style={{ background: checked ? "rgba(124,92,58,0.08)" : "transparent" }}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(h.id)}
                data-testid={`scheduler-add-horse-${h.id}`}
              />
              <span style={{ width: 10, height: 10, borderRadius: 5, background: h.calendar_color, display: "inline-block" }} />
              <span className="text-sm">{h.name}</span>
            </label>
          );
        })}
      </div>

      <label className="se-label block mb-1">Notes</label>
      <textarea className="se-input mb-3" style={{ height: 70, padding: "10px 14px" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="scheduler-add-notes" />
    </>
  );
}

function LeaveFields({ options, selectorLabel, selectorTestId, selectedId, onSelect, form, setForm, disabledSelector }) {
  return (
    <>
      <label className="se-label block mb-1">{selectorLabel}</label>
      <select
        className="se-input mb-3"
        value={selectedId}
        onChange={(e) => onSelect(e.target.value)}
        required
        disabled={disabledSelector}
        data-testid={selectorTestId}
      >
        <option value="">— Select —</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="se-label block mb-1">Start date *</label>
          <input type="date" className="se-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="scheduler-leave-start" />
        </div>
        <div>
          <label className="se-label block mb-1">End date *</label>
          <input type="date" className="se-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required min={form.start_date || undefined} data-testid="scheduler-leave-end" />
        </div>
      </div>

      <label className="se-label block mb-1">Reason (optional)</label>
      <input className="se-input mb-3" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="scheduler-leave-reason" />
    </>
  );
}

// -----------------------------------------------------------------------------
// Event detail — centred popup on desktop, slide-up bottom sheet on mobile.
// -----------------------------------------------------------------------------
function EventDetailModal({ event, canWrite, horseById, mobile, onClose, onDeleted, onEdit, navigate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const et = event.event_type;
  const isCustomEvent = et === "training" || et === "custom";
  const isStaffLeave = et === "staff_leave";
  const isOwnerAway = et === "owner_away";
  const detail = event.detail || {};

  const gotoRecord = () => {
    if (detail.vet_visit_id && event.horse_id) navigate(`/horses/${event.horse_id}`);
    else if (detail.trip_id) navigate(`/trips/${detail.trip_id}`);
    else if (event.horse_id) navigate(`/horses/${event.horse_id}`);
  };

  const remove = async () => {
    setBusy(true); setErr("");
    try {
      if (isCustomEvent) {
        const id = detail.scheduler_event_id || String(event.id).replace(/^sched:/, "").split(":")[0];
        await api.delete(`/scheduler/events/${id}`);
      } else if (isStaffLeave) {
        await api.patch(`/users/${detail.user_id}`, {
          leave_start: null, leave_end: null, leave_reason: null,
        });
      } else if (isOwnerAway) {
        await api.patch(`/owners/${detail.owner_id}`, {
          leave_start: null, leave_end: null, leave_reason: null,
        });
      }
      onDeleted();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startEdit = () => {
    if (isCustomEvent) {
      onEdit({
        mode: "edit",
        kind: "custom",
        scheduler_event_id: detail.scheduler_event_id,
        title: detail.raw_title || event.title.replace(/^[^\s]+\s/, ""),
        start_date: detail.start_date || String(event.start).slice(0, 10),
        end_date: detail.end_date || "",
        all_day: event.allDay !== false,
        start_time: detail.start_time || "",
        end_time: detail.end_time || "",
        horse_ids: Array.isArray(detail.horse_ids) ? detail.horse_ids : (event.horse_id ? [event.horse_id] : []),
        notes: detail.notes || "",
      });
    } else if (isStaffLeave) {
      onEdit({
        mode: "edit",
        kind: "staff_leave",
        user_id: detail.user_id,
        full_name: event.title,
        leave_start: event.start,
        leave_end: event.end || event.start,
        leave_reason: detail.leave_reason || "",
      });
    } else if (isOwnerAway) {
      onEdit({
        mode: "edit",
        kind: "owner_away",
        owner_id: detail.owner_id,
        full_name: event.title,
        leave_start: event.start,
        leave_end: event.end || event.start,
        leave_reason: detail.leave_reason || "",
      });
    }
  };

  const canEditOrDelete = canWrite && (isCustomEvent || isStaffLeave || isOwnerAway);
  const horse = event.horse_id ? horseById.get(event.horse_id) : null;

  const body = (
    <>
      <div className="flex items-center justify-between mb-3">
        <span className="se-badge se-badge-inactive" data-testid="scheduler-event-type">{et.replace(/_/g, " ")}</span>
        <button type="button" onClick={onClose} aria-label="Close"><X size={16} /></button>
      </div>
      <h3 className="font-heading mb-1" style={{ fontSize: 18 }}>{event.title}</h3>
      <div className="text-sm mb-3" style={{ color: "#8C9E8A" }}>
        {String(event.start).slice(0, 10)}{event.end && event.end !== event.start ? ` → ${String(event.end).slice(0, 10)}` : ""}
        {detail.event_time && <span className="ml-2">· {detail.event_time}</span>}
        {detail.start_time && !detail.event_time && <span className="ml-2">· {detail.start_time}{detail.end_time ? `–${detail.end_time}` : ""}</span>}
      </div>
      {horse && (
        <div className="text-sm mb-2 inline-flex items-center gap-1" data-testid="scheduler-event-horse">
          <span style={{ width: 10, height: 10, borderRadius: 5, background: horse.calendar_color, display: "inline-block" }} />
          {horse.name}
        </div>
      )}
      {detail.vet_name       && <Row label="Vet"        value={detail.vet_name} />}
      {detail.reason         && <Row label="Reason"     value={detail.reason} />}
      {detail.vaccine_name   && <Row label="Vaccine"    value={detail.vaccine_name} />}
      {detail.batch_number   && <Row label="Batch #"    value={detail.batch_number} />}
      {detail.administered_by && <Row label="Given by"  value={detail.administered_by} />}
      {detail.care_type      && <Row label="Care type"  value={detail.care_type} />}
      {detail.trip_name      && <Row label="Trip"       value={detail.trip_name} />}
      {detail.location       && <Row label="Location"   value={detail.location} />}
      {detail.restriction_type && <Row label="Restriction" value={String(detail.restriction_type).replace(/_/g, " ")} />}
      {detail.training_type  && <Row label="Training"   value={String(detail.training_type).replace(/_/g, " ")} />}
      {detail.leave_reason   && <Row label="Reason"     value={detail.leave_reason} />}
      {detail.notes          && <Row label="Notes"      value={detail.notes} />}
      {detail.comment        && <Row label="Comment"    value={detail.comment} />}
      {err && <div className="text-xs mt-2 px-2 py-1 rounded" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}>{err}</div>}
      <div className="flex justify-end gap-2 mt-4 flex-wrap">
        {(event.horse_id || detail.trip_id) && (
          <button type="button" className="se-btn se-btn-ghost" onClick={gotoRecord} data-testid="scheduler-view-record">View full record</button>
        )}
        {canEditOrDelete && (
          <button type="button" className="se-btn se-btn-ghost" onClick={startEdit} data-testid="scheduler-edit-event">
            Edit
          </button>
        )}
        {canEditOrDelete && (
          <button type="button" className="se-btn" style={{ background: "#C0392B" }} disabled={busy} onClick={remove} data-testid="scheduler-delete-event">
            <Trash2 size={14} /> {busy ? "Deleting…" : "Delete"}
          </button>
        )}
      </div>
    </>
  );

  if (mobile) {
    return (
      <div
        className="fixed inset-0 z-40 flex items-end justify-center"
        style={{ background: "rgba(0,0,0,0.4)" }}
        onClick={onClose}
        data-testid="scheduler-event-detail-sheet"
      >
        <div
          className="w-full se-card scheduler-sheet"
          style={{
            borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
            borderTopLeftRadius: 18, borderTopRightRadius: 18,
            maxHeight: "85vh", overflowY: "auto",
            animation: "sched-sheet-up 220ms ease-out",
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="scheduler-event-detail"
        >
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <span style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(124,92,58,0.3)", display: "block" }} />
          </div>
          {body}
          <style>{`@keyframes sched-sheet-up { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="se-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="scheduler-event-detail">
        {body}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="text-sm mb-1"><span className="se-label mr-2">{label}:</span>{String(value)}</div>
);

function addOneDay(iso) {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + 1));
    return dt.toISOString().slice(0, 10);
  } catch { return iso; }
}
